import { useState, useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useAllOrganizationsQuery, useDronesWithOrganizationsQuery } from '../adminTools.queries';
import {
  useUpdateDroneOrganizationMutation,
  useUpdateDroneOwnershipMutation,
} from '../adminTools.mutations';
import type {
  DroneWithOrganization,
  OrganizationsResponse,
  OwnershipType,
} from '../types/adminTools.types';

export interface UseDroneAssignmentsReturn {
  // Dialog state
  dialogOpen: boolean;
  dronesWithOrgs: DroneWithOrganization[];
  loading: boolean;
  error: string;

  // Edit state for organization
  editingSerial: string | null;
  selectedOrg: string;
  updating: boolean;

  // Edit state for ownership
  editingOwnershipSerial: string | null;
  selectedOwnershipType: OwnershipType | null;
  selectedLeaseEndDate: string;
  updatingOwnership: boolean;

  // Organizations data (fetched with drones for dropdown)
  orgsData: OrganizationsResponse | null;

  // Actions for organization
  openDialog: () => Promise<void>;
  closeDialog: () => void;
  startEdit: (serial: string, currentOrgId: string | null) => void;
  saveOrg: (serial: string) => Promise<void>;
  cancelEdit: () => void;
  setSelectedOrg: (orgId: string) => void;
  setError: (error: string) => void;

  // Actions for ownership
  startOwnershipEdit: (
    serial: string,
    currentOwnershipType: OwnershipType | null,
    currentLeaseEndDate: string | null
  ) => void;
  saveOwnership: (serial: string) => Promise<void>;
  cancelOwnershipEdit: () => void;
  setSelectedOwnershipType: (type: OwnershipType | null) => void;
  setSelectedLeaseEndDate: (date: string) => void;
}

export function useDroneAssignments(): UseDroneAssignmentsReturn {
  const { showError, showSuccess } = useErrorHandler();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');

  // Server data — fetched only while the dialog is open.
  const dronesQuery = useDronesWithOrganizationsQuery(dialogOpen);
  const orgsQuery = useAllOrganizationsQuery(dialogOpen);
  const dronesWithOrgs = dronesQuery.data ?? [];
  const orgsData = orgsQuery.data ?? null;
  const loading = dialogOpen && (dronesQuery.isLoading || orgsQuery.isLoading);

  const updateOrgMutation = useUpdateDroneOrganizationMutation();
  const updateOwnershipMutation = useUpdateDroneOwnershipMutation();

  // Edit state for organization
  const [editingSerial, setEditingSerial] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  // Edit state for ownership
  const [editingOwnershipSerial, setEditingOwnershipSerial] = useState<string | null>(null);
  const [selectedOwnershipType, setSelectedOwnershipType] = useState<OwnershipType | null>(null);
  const [selectedLeaseEndDate, setSelectedLeaseEndDate] = useState<string>('');

  const openDialog = useCallback(async () => {
    setDialogOpen(true);
    setError('');
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setError('');
    setEditingSerial(null);
    setSelectedOrg('');
    setEditingOwnershipSerial(null);
    setSelectedOwnershipType(null);
    setSelectedLeaseEndDate('');
  }, []);

  const startEdit = useCallback((serial: string, currentOrgId: string | null) => {
    setEditingSerial(serial);
    setSelectedOrg(currentOrgId || '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingSerial(null);
    setSelectedOrg('');
  }, []);

  const saveOrg = useCallback(
    async (serial: string) => {
      if (!selectedOrg) {
        setError('Please select an organization');
        return;
      }

      setError('');

      try {
        // Mutation invalidates drones-with-organizations, refreshing the list.
        await updateOrgMutation.mutateAsync({ serialNumber: serial, organizationId: selectedOrg });

        setEditingSerial(null);
        setSelectedOrg('');
        showSuccess('Drone organization updated successfully');
      } catch (err) {
        showError(err, 'Failed to update drone organization');
      }
    },
    [selectedOrg, updateOrgMutation, showError, showSuccess]
  );

  // Ownership editing functions
  const startOwnershipEdit = useCallback(
    (
      serial: string,
      currentOwnershipType: OwnershipType | null,
      currentLeaseEndDate: string | null
    ) => {
      setEditingOwnershipSerial(serial);
      setSelectedOwnershipType(currentOwnershipType);
      setSelectedLeaseEndDate(currentLeaseEndDate || '');
    },
    []
  );

  const cancelOwnershipEdit = useCallback(() => {
    setEditingOwnershipSerial(null);
    setSelectedOwnershipType(null);
    setSelectedLeaseEndDate('');
  }, []);

  const saveOwnership = useCallback(
    async (serial: string) => {
      setError('');

      try {
        // If ownership type is SOLD, clear the lease end date
        const leaseEndDate =
          selectedOwnershipType === 'LEASED' ? selectedLeaseEndDate || null : null;

        // Mutation invalidates drones-with-organizations, refreshing the list.
        await updateOwnershipMutation.mutateAsync({
          serialNumber: serial,
          ownershipType: selectedOwnershipType,
          leaseEndDate,
        });

        setEditingOwnershipSerial(null);
        setSelectedOwnershipType(null);
        setSelectedLeaseEndDate('');
        showSuccess('Drone ownership updated successfully');
      } catch (err) {
        showError(err, 'Failed to update drone ownership');
      }
    },
    [selectedOwnershipType, selectedLeaseEndDate, updateOwnershipMutation, showError, showSuccess]
  );

  return {
    dialogOpen,
    dronesWithOrgs,
    loading,
    error,
    editingSerial,
    selectedOrg,
    updating: updateOrgMutation.isPending,
    editingOwnershipSerial,
    selectedOwnershipType,
    selectedLeaseEndDate,
    updatingOwnership: updateOwnershipMutation.isPending,
    orgsData,
    openDialog,
    closeDialog,
    startEdit,
    saveOrg,
    cancelEdit,
    setSelectedOrg,
    setError,
    startOwnershipEdit,
    saveOwnership,
    cancelOwnershipEdit,
    setSelectedOwnershipType,
    setSelectedLeaseEndDate,
  };
}
